import React, {
    useRef,
    useEffect,
    useState,
    useMemo,
    useCallback,
    useContext,
} from 'react';
import {
    ThemeProvider,
    Grid,
    GridColumn,
    View,
    useViewport,
} from '@fairygodboss/ui';
import { JobCardWrapper } from './JobCardWrapper';
import { JobsSearchBarWrapper } from './JobsSearchBarWrapper';
import { JobsSearchFilterWrapper } from './JobsSearchFilterWrapper';
import { JobsSearchResultsWrapper } from './JobsSearchResultsWrapper';
import DirectApplyModal from '../SingleJob/SingleJobTemplateV2/DirectApplyModal';
import { useJobSearchContext } from '../../../contexts/JobSearchContext';
import { PageContext } from '../../../contexts/ContextCreator';

const DesktopRender = ({ jobSearchHeight, containerRef }) => {
    return useMemo(
        () => (
            <View
                fontFamily="'Open Sans', sans-serif"
                display="block"
                bg="#f2f2f2"
                ref={containerRef}
                height={`${jobSearchHeight}px`}
                css={{
                    WebkitFontSmoothing: 'antialiased',
                    '* ::-webkit-scrollbar': {
                        '-webkit-appearance': 'none',
                        width: '7px',
                        height: '7px',
                    },
                    '* ::-webkit-scrollbar-thumb': {
                        borderRadius: '4px',
                        backgroundColor: 'rgba(0,0,0,.5)',
                        '-webkit-box-shadow': '0 0 1px rgba(255,255,255,.5)',
                    },
                }}
            >
                <Grid
                    removeGutterPadding
                    layout="fixed"
                    background="white"
                    paddingLeft="0px"
                    paddingRight="0px"
                    height="100%"
                >
                    <GridColumn
                        size={12}
                        height="100%"
                        overflow="hidden"
                        paddingLeft="0px"
                        paddingRight="0px"
                        display="flex"
                        css={{ flexFlow: 'column' }}
                    >
                        <View>
                            <JobsSearchBarWrapper />
                        </View>
                        <View>
                            <JobsSearchFilterWrapper />
                        </View>
                        <View
                            minHeight="100px"
                            display="flex"
                            flexFlow="column"
                            flex="1 1 auto"
                            css={{ flexFlow: 'column' }}
                        >
                            <Grid
                                removeGutterPadding
                                height="100%"
                                width="100%"
                            >
                                <JobsSearchResultsWrapper />
                                <GridColumn
                                    size={{ _: 12, md: 7 }}
                                    height="100%"
                                    display="flex"
                                    flexDirection="column"
                                    overflowY="auto"
                                    position="initial"
                                >
                                    <JobCardWrapper />
                                </GridColumn>
                            </Grid>
                        </View>
                    </GridColumn>
                </Grid>
            </View>
        ),
        [jobSearchHeight, containerRef]
    );
};

const MobileRender = () =>
    useMemo(
        () => (
            <View
                display="block"
                bg="#f2f2f2"
                fontFamily="'Open Sans', sans-serif"
                style={{
                    WebkitFontSmoothing: 'antialiased',
                }}
            >
                <JobCardWrapper />
                <Grid
                    removeGutterPadding
                    layout="fixed"
                    background="white"
                    paddingLeft="0px"
                    paddingRight="0px"
                    height="100%"
                    maxWidth="584px"
                >
                    <GridColumn
                        size={12}
                        height="100%"
                        paddingLeft="0px"
                        paddingRight="0px"
                        display="flex"
                        css={{ flexFlow: 'column' }}
                    >
                        <View>
                            <JobsSearchBarWrapper />
                        </View>
                        <View>
                            <JobsSearchFilterWrapper />
                        </View>
                        <View
                            display="flex"
                            flexFlow="column"
                            flex="1 1 auto"
                            css={{ flexFlow: 'column' }}
                        >
                            <Grid removeGutterPadding width="100%">
                                <JobsSearchResultsWrapper />
                            </Grid>
                        </View>
                    </GridColumn>
                </Grid>
            </View>
        ),
        []
    );

export const JobsSearch = () => {
    const containerRef = useRef();
    const [jobSearchHeight, setJobSearchHeight] = useState(
        window.innerHeight - 56
    );
    const { isDirectApplyOpen } = useJobSearchContext();
    const { setDirectApplyJobSearchOpen } = useContext(PageContext);

    useEffect(() => {
        setDirectApplyJobSearchOpen(isDirectApplyOpen);
    }, [isDirectApplyOpen, setDirectApplyJobSearchOpen]);

    const windowResize = useCallback(() => {
        requestAnimationFrame(() => {
            const el = containerRef.current;
            if (el) {
                const topOffest = el?.getBoundingClientRect()?.top;
                const windowHeight = window.innerHeight;
                const _jobSearchHeight = windowHeight - topOffest;
                setJobSearchHeight(_jobSearchHeight);
            }
        });
    }, [containerRef]);

    useEffect(() => {
        windowResize();
        window.addEventListener('resize', windowResize, false);
        return () => window.removeEventListener('resize', windowResize);
    }, [windowResize]);

    const { isSM } = useViewport();

    return (
        <ThemeProvider>
            {!isSM ? (
                <MobileRender />
            ) : (
                <DesktopRender
                    jobSearchHeight={jobSearchHeight}
                    containerRef={containerRef}
                />
            )}
            <DirectApplyModal />
        </ThemeProvider>
    );
};
